import { FrequencyUnit, PatientKind } from "common/types";
import { Optional } from "common/utils/types";

/** Matches medication_adherence.constants MedicationDosageReminderStatus */
export const DosageReminderStatus = {
  Active: "ACTIVE",
  Inactive: "INACTIVE",
  Paused: "PAUSED",
} as const;
export type DosageReminderStatus = (typeof DosageReminderStatus)[keyof typeof DosageReminderStatus];

export type DeliveryMethod = "text" | "email";

export type IsDeliveryMethodSelected = (method: DeliveryMethod) => boolean;

export type ReminderFormValues = {
  isActive: boolean;
  nextDoseDate: Date | null;
  repeatEvery: string;
  intervalUnit: FrequencyUnit;
  stopDate: Date | null;
  deliveryMethods: Record<DeliveryMethod, boolean>;
};

/** Matches BasicCatalogImageSerializer */
export type DosageReminderItemImage = {
  id: number;
  url: string;
  thumbnail_url: string;
  medium_url: string;
  large_url: string;
};

/** Matches MedicationDosageReminderPatientReadSerializer */
export type DosageReminderPatient = {
  id: number;
  name: string;
  is_active: boolean;
  species: PatientKind;
};

/** Matches MedicationDosageReminderItemProductLineReadSerializer */
export type DosageReminderItemProductLine = {
  id: number;
  name: string;
};

/** Matches MedicationDosageReminderItemProductReadSerializer */
export type DosageReminderItemProduct = {
  id: number;
  name: string;
  product_line: DosageReminderItemProductLine;
};

/** Matches MedicationDosageReminderItemReadSerializer */
export type DosageReminderItem = {
  id: number;
  name: string;
  product: DosageReminderItemProduct;
  image: Optional<DosageReminderItemImage>;
};

/** Matches MedicationDosageReminderReadSerializer */
export type DosageReminder = {
  id: number;
  patient: DosageReminderPatient;
  item: DosageReminderItem;
  status: DosageReminderStatus;
  is_active: boolean;
  inactive_date: Optional<string>;
  frequency: number;
  interval_unit: FrequencyUnit;
  next_dose_date: Optional<string>;
  stop_date: Optional<string>;
  email_enabled: boolean;
  text_enabled: boolean;
  last_sent_date: Optional<string>;
  is_deleted: boolean;
};

/** Matches MedicationDosageReminderPatientItemReadSerializer */
export type DosageReminderPatientItem = {
  patient: DosageReminderPatient;
  item: DosageReminderItem;
};

/** Matches query params for GET /api/client/v1/medication-adherence/medication-dosage-reminders/ */
export type DosageRemindersSearch = {
  patient_id?: number;
  item_id?: number;
};

/** Matches query params for GET /api/client/v1/medication-adherence/medication-dosage-reminders/patient-item/ */
export type DosageReminderPatientItemSearch = {
  patient_id: number;
  item_id: number;
};

/** Identifies the reminder to show in the reminder drawer */
export type DosageReminderLookup = { id: number } | { item_id: number; patient_id: number };

/** Search params on the reminders page that open the reminder drawer */
export type DosageRemindersPageSearch = {
  id?: number;
  item?: number;
  patient?: number;
};

/** Matches MedicationDosageReminderCreateSerializer */
export type DosageReminderCreate = {
  patient: number;
  item: number;
  frequency: number;
  interval_unit: FrequencyUnit;
  next_dose_date: string | null;
  stop_date?: string | null;
  email_enabled?: boolean;
  text_enabled?: boolean;
};

/** Matches MedicationDosageReminderUpdateSerializer */
export type DosageReminderUpdate = {
  status?: DosageReminderStatus;
  frequency?: number;
  interval_unit?: FrequencyUnit;
  next_dose_date?: string | null;
  stop_date?: string | null;
  email_enabled?: boolean;
  text_enabled?: boolean;
};
